import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import ADLRecordForm from "./ADLRecordForm";
import { createAdlRecord, fetchAdlActivities, clearError } from "../../../store/slices/adlRecordsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice";

const AddADLRecord = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    // ✅ Redux state
    const {
        adlActivities,
        isLoading,
        error
    } = useSelector((state) => state.adlRecords);

    const [formData, setFormData] = useState({
        patientId, // initialize
        caseId,    // initialize
        clientName: "",
        roomNumber: "",
        dateRange: "",
        checkedBy: "",
        nursingManager: "",
        activities: [],
    });

    const [errors, setErrors] = useState({});

    // ✅ Fetch ADL activities on component mount & fetch patient details
    useEffect(() => {
        dispatch(fetchAdlActivities());
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient:", err));
        }
    }, [dispatch, patientId]);

    useEffect(() => {
        if (adlActivities && adlActivities.length > 0 && formData.activities.length === 0) {
            const initialActivities = adlActivities.map(activity => ({
                activity_id: activity.id,
                date: new Date().toISOString().split('T')[0],       // User fill karega ya null jayega
                score: "",      // User fill karega ya null jayega
                totalScore: "",
                staffName: ""
            }));

            setFormData(prev => ({
                ...prev,
                activities: initialActivities
            }));
        }
    }, [adlActivities]);

    // ✅ Clear errors when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    // ✅ Handle basic form changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // ✅ Handle activities data from child component
    const handleActivitiesChange = React.useCallback((activitiesData) => {
        setFormData(prev => {
            // Sirf tab update karein agar data waqai different ho
            if (JSON.stringify(prev.activities) === JSON.stringify(activitiesData)) {
                return prev;
            }
            return {
                ...prev,
                activities: activitiesData
            };
        });
    }, []);

    // ✅ Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.clientName?.trim()) {
            newErrors.clientName = "Client name is required.";
        }
        if (!formData.roomNumber?.trim()) {
            newErrors.roomNumber = "Room number is required.";
        }
        if (!formData.dateRange?.trim()) {
            newErrors.dateRange = "Date range is required.";
        }
        if (!formData.activities || formData.activities.length === 0) {
            newErrors.activities = "At least one activity record is required.";
        }
        // Sirf itna check kaafi hai ke array empty na ho
        if (!formData.activities || formData.activities.length === 0) {
            newErrors.activities = "Activity records are missing.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ✅ Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before submitting.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            // Prepare the payload for API - transform to match your backend expectations
            const payload = {
                patient_id: formData.patientId || null,
                case_id: formData.caseId || null,
                client_name: formData.clientName,
                room_number: formData.roomNumber,
                date_range: formData.dateRange,
                checked_by: formData.checkedBy || null,
                nursing_manager: formData.nursingManager || null,
                activities: formData.activities.map(activity => ({
                    activity_id: activity.activity_id,
                    date: activity.date || null,      // Empty fields will be null
                    score: activity.score || null,
                    total_score: activity.totalScore || null,
                    staff_name: activity.staffName || null
                }))
            };

            await dispatch(createAdlRecord(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "ADL record added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/cases/${payload.case_id}/profile`, { state: { patientId: payload.patient_id, caseId: payload.case_id } });

        } catch (error) {
            console.error("Failed to create ADL record:", error);

            let errorMessage = "Failed to create ADL record";
            if (typeof error === 'object' && error !== null) {
                // Handle validation errors from backend
                const firstError = Object.values(error)[0]?.[0];
                if (firstError) {
                    errorMessage = firstError;
                }
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Try Again",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {error && (
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {error}
                            </div>
                        )}

                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <ADLRecordForm
                                formData={formData}
                                handleChange={handleChange}
                                handleActivitiesChange={handleActivitiesChange}
                                adlActivities={adlActivities}
                                isLoading={isLoading}
                                isEdit={false}
                                errors={errors}
                            />
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddADLRecord;